import { useEffect } from "react";
import PropTypes from "prop-types";
import { format, differenceInMilliseconds } from "date-fns";
import LinkWrap from "../../../LinkWrap/component";
import { fetch_schedule } from "../../../../actions/Schedule/actions";
import { useDispatch, useSelector } from "react-redux";
import { makeAbsoluteCBCDomain } from "../../../../utils/path/library";
import { FULL_SCHEDULES_LINK } from "./constants";
import { Heading } from "../../../Heading/component";

export const ScheduleRightRail = ({
    type = "",
    regionName = "",
    scheduleConfig = {},
    headline = "Broadcast Schedule",
}) => {
    const dispatch = useDispatch();
    const schedule = useSelector((state) => state.schedule);
    useEffect(() => {
        dispatch(fetch_schedule(type, regionName));
    }, [regionName]);

    /**
     * Renders the list of SPORT scheduled items, fetched from the state
     * @param {object} scheduleObject The object containing schedule items
     * @return {JSX} The list of the SPORT scheduled items
     */
    const renderSportsSchedule = (scheduleObject) => {
        // Go over the list of scheduled items from the state and generate a list
        // based on the sportFilter prop value. If there is no sportFilter prop,
        // return all scheduled items from the state.
        const scheduleItems = scheduleObject.scheduleList
            .filter((item) => {
                if (
                    scheduleConfig.filter &&
                    scheduleConfig.filter.constructor === String
                ) {
                    return item.types.indexOf(scheduleConfig.filter) > -1;
                } else if (
                    scheduleConfig.filter &&
                    scheduleConfig.filter.constructor === Array
                ) {
                    const len = scheduleConfig.filter.length;
                    for (let i = 0; i < len; i++) {
                        return (
                            item.types.indexOf(scheduleConfig.filter[i]) > -1
                        );
                    }
                }
                return true;
            })
            .reduce((acc, item) => {
                const broadcastStart = new Date(item.startTime);
                const broadcastEnd = new Date(item.endTime);
                const now = new Date();
                // If the broadcast has ended, don't show the item in the list
                if (differenceInMilliseconds(now, broadcastEnd) > 0) {
                    return acc;
                }
                // If the current time is between the start and end of the broadcast, display a link to the story
                let title = <span className="scheduleTitle">{item.title}</span>;
                if (
                    differenceInMilliseconds(now, broadcastStart) >= 0 &&
                    differenceInMilliseconds(now, broadcastEnd) <= 0
                ) {
                    title = (
                        <LinkWrap
                            url={makeAbsoluteCBCDomain(item.url)}
                            external={true}
                            className="scheduleLink"
                        >
                            {item.title}
                        </LinkWrap>
                    );
                }
                const broadcastTime = format(broadcastStart, "MMM dd, h:mm a");
                const media = item.media ? item.media.join(", ") : null;

                acc.push(
                    <li className="scheduleListItem" key={item.id}>
                        <span className="scheduleMedia">{media}</span>
                        <span className="scheduleTime">{broadcastTime}</span>
                        {title}
                    </li>
                );
                return acc;
            }, []);
        // Render nothing if there are no items in the broadcast schedule
        if (scheduleItems.length < 1) {
            return null;
        }

        const fullScheduleUrl =
            scheduleConfig && scheduleConfig.fullScheduleUrl
                ? scheduleConfig.fullScheduleUrl
                : FULL_SCHEDULES_LINK;
        // Render the markup of the schedule list
        return (
            <div className="schedule sportsSchedule">
                <Heading
                    className="scheduleTitle"
                    text={headline}
                    level="h2"
                    levelClassName="h3"
                />
                <ul className="scheduleList">{scheduleItems}</ul>
                <div className="disclaimer">
                    Times shown in EDT, unless otherwise noted. Schedule is
                    subject to change without notice.
                    <LinkWrap
                        external={true}
                        url={fullScheduleUrl}
                        className="scheduleLink sclt-sportsSchedule"
                    >
                        Full Schedule &raquo;
                    </LinkWrap>
                </div>
            </div>
        );
    };

    // Lookup the current section we're interested in and return that scheduleList from the state
    const sectionSchedule = type && schedule ? schedule[type] : null;
    if (!sectionSchedule || !sectionSchedule.scheduleList) {
        return null;
    }
    if (type === "sports") {
        return renderSportsSchedule(sectionSchedule);
    }
    return null;
};

ScheduleRightRail.propTypes = {
    type: PropTypes.string,
    headline: PropTypes.string,
    regionName: PropTypes.string,
    scheduleConfig: PropTypes.object,
};

export default ScheduleRightRail;
